import {
  Box,
  Card,
  Circle,
  FormatNumber,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { PropsWithChildren } from "react";
import { LuTimer } from "react-icons/lu";

import { Countdown } from "@/components/countdown";
import { RoundedBadge } from "@/components/rounded-badge";
import { TextWithLoadingState } from "@/components/text-with-loading-state";
import { useGuardiansCampaignStats } from "@/hooks/useGuardiansCampaignStats";

export const Hero = () => {
  const { guardiansCampaignStats } = useGuardiansCampaignStats();

  const {
    campaign,
    txCount = 0,
    totalQualifiedUsers,
    campaignEndDate,
    campaignComplete,
  } = guardiansCampaignStats ?? {};
  const { startTx = 0, targetTx = 0, startDate, endDate } = campaign ?? {};
  const campaignStartDate = startDate ? new Date(startDate) : undefined;

  const percentComplete = ((txCount - startTx) * 100) / (targetTx - startTx);
  return (
    <VStack
      justifyContent={["center", "center", "center", "space-between"]}
      w="full"
      bgImage="url('/assets/guardians/hero.svg')"
      backgroundPosition="center"
      backgroundSize="cover"
      bgColor="#212C2D"
      borderRadius="3xl"
      overflow="hidden"
      color="fg.inverted"
      h={[516, 447]}
    >
      <VStack maxW={600} gap={4} textAlign="center" py={10} px={4} w="full">
        {endDate && !campaignComplete && (
          <HStack gap={1} alignItems="center">
            <Icon boxSize={5} color="fg.inverted">
              <LuTimer />
            </Icon>
            <Countdown format="DH" date={new Date(endDate)} />
          </HStack>
        )}
        <Heading fontWeight="700" size="4xl">
          Guardians of Plume
        </Heading>
        <Text fontFamily="heading" fontSize="lg" fontWeight="500" opacity={0.8}>
          Join the movement, earn rewards, unlock badges, and make every
          transaction count!
        </Text>
        <Card.Root
          bg="rgba(255, 255, 255, 0.04)"
          border="none"
          rounded="3xl"
          w="full"
        >
          <Card.Body textAlign="center" py={4}>
            <HStack w="full" justifyContent="center" alignItems="center" mb={4}>
              ⭐️
              <TextWithLoadingState
                color="bg"
                fontFamily="heading"
                fontSize="lg"
                fontWeight="500"
                loading={!startTx || !targetTx}
              >
                Goal: <FormatNumber notation="compact" value={startTx} /> →{" "}
                <FormatNumber notation="compact" value={targetTx} />{" "}
                transactions
              </TextWithLoadingState>
              ⭐️
            </HStack>
            <RoundedBadge
              color="fg.inverted"
              h={10}
              bgColor="rgba(255, 255, 255, 0.1)"
              fontSize="md"
              overflow="hidden"
              position="relative"
              px={6}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                h="100%"
                w={`${Math.min(percentComplete, 100)}%`}
                bgColor="#5A3CE8"
                zIndex={0}
                transition="width 0.3s ease"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {campaignComplete && (
                  <Text
                    fontWeight="500"
                    position="relative"
                    zIndex={1}
                    color="fg.inverted"
                  >
                    Campaign Complete!
                  </Text>
                )}
                {!campaignComplete && percentComplete >= 20 && (
                  <Text
                    fontWeight="500"
                    position="relative"
                    zIndex={1}
                    color="fg.inverted"
                  >
                    <FormatNumber value={txCount} notation="compact" /> TXs
                  </Text>
                )}
              </Box>

              {percentComplete < 20 && (
                <Text
                  fontWeight="500"
                  position="relative"
                  zIndex={1}
                  color="fg.inverted"
                >
                  <FormatNumber value={txCount} notation="compact" /> TXs
                </Text>
              )}
            </RoundedBadge>
          </Card.Body>
        </Card.Root>
        <HStack
          w="full"
          justifyContent={[
            "space-evenly",
            "space-evenly",
            "space-evenly",
            campaignComplete ? "space-evenly" : "space-between",
          ]}
          flexWrap="wrap"
        >
          <CampaignInstructionStep
            stepNumber={1}
            campaignComplete={campaignComplete}
          >
            {campaignComplete ? (
              <>
                ✅ <FormatNumber value={txCount} notation="compact" />{" "}
                transactions reached
              </>
            ) : (
              "Make transactions"
            )}
          </CampaignInstructionStep>
          <CampaignInstructionStep
            stepNumber={2}
            campaignComplete={campaignComplete}
          >
            {campaignComplete
              ? `📅  Campaign ran from ${formatDate(campaignStartDate)}-${formatDate(campaignEndDate)}`
              : "Climb the tiers"}
          </CampaignInstructionStep>
          <CampaignInstructionStep
            stepNumber={3}
            campaignComplete={campaignComplete}
          >
            {campaignComplete ? (
              <>
                🏆 <FormatNumber value={totalQualifiedUsers ?? 0} />{" "}
                participants
              </>
            ) : (
              "Earn rewards"
            )}
          </CampaignInstructionStep>
        </HStack>
      </VStack>
    </VStack>
  );
};

const CampaignInstructionStep = ({
  stepNumber,
  children,
  campaignComplete,
}: {
  stepNumber: number;
  campaignComplete?: boolean;
} & PropsWithChildren) => {
  return (
    <RoundedBadge
      color="fg.inverted"
      h={10}
      bgColor="rgba(255, 255, 255, 0.04)"
      fontSize="md"
      p={2}
      px={4}
      gap={2}
    >
      {!campaignComplete && (
        <Circle size={6} bgColor="bg.guardiansCampaign">
          {stepNumber}
        </Circle>
      )}{" "}
      {children}
    </RoundedBadge>
  );
};

const formatDate = (date?: Date) => {
  if (!date) return "N/A";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
};
